/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, {useState} from 'react'
import styles from './style/index.css'
import {List, ColumnSpec} from './List'
import classNames from 'classnames'
import {LevelHeader, LevelRow, LevelHeaderProps} from './columns/Level'
import {SearchHeader, MessageRow, SearchHeaderProps} from './columns/Message'
import {ServiceHeader, ServiceRow, ServiceHeaderProps} from './columns/Service'
import {DateHeader, DateRow, DateHeaderProps} from './columns/Date'
import {ZIndexProps} from './columns/model'
import intl from './utils/intl/intl'

export enum LogLevel {
  Error = 'error',
  Warn = 'warn',
  Info = 'info',
  Debug = 'debug'
}

export interface LogListItem {
  id: string
  level: LogLevel
  message: string
  eventType: string
  service: string
  payload: Record<string, any>
  createdAt: Date
}

const getListColumnsSpec = (
  props: LogsListProps,
  isExpanded: (item: LogListItem) => boolean
): Array<ColumnSpec<LogListItem>> => [
  {
    id: 'level',
    width: 40,
    HeaderComponent: props.headerHidden ? null : LevelHeader,
    Component: LevelRow,
    ...props
  },
  {
    id: 'message',
    HeaderComponent: props.headerHidden ? null : SearchHeader,
    Component: ({item}) => <MessageRow item={item} showDetails={isExpanded(item)} />,
    ...props
  },
  {
    id: 'service',
    width: 200,
    HeaderComponent: props.headerHidden ? null : ServiceHeader,
    Component: ServiceRow,
    ...props
  },
  {
    id: 'date',
    width: 200,
    HeaderComponent: props.headerHidden ? null : DateHeader,
    Component: DateRow,
    ...props
  }
]

export type LogsListProps = ServiceHeaderProps &
  ZIndexProps &
  DateHeaderProps &
  LevelHeaderProps &
  SearchHeaderProps & {
    items: LogListItem[]
    onLoadMore: (page: number) => void
    hasMoreData: boolean
    headerHidden?: boolean
  }

export const LogsList = (props: LogsListProps) => {
  const {items, hasMoreData, onLoadMore} = props
  const [expandedState, setExpandedState] = useState(new Set<string>())

  if (items.length === 0 && expandedState.size > 0) {
    setExpandedState(new Set())
  }

  return (
    <div className={classNames(styles['log'])}>
      <List
        key="log-list"
        columnSpecs={getListColumnsSpec(props, (item: LogListItem) => {
          return expandedState.has(item.id)
        })}
        items={items}
        onRowClick={(item) => {
          if (expandedState.has(item.id)) {
            expandedState.delete(item.id)
          } else {
            expandedState.add(item.id)
          }
          setExpandedState(new Set(expandedState))
        }}
        hasMoreData={hasMoreData}
        onLoadMore={onLoadMore}
        expandedState={expandedState}
      />
      {items.length === 0 && !hasMoreData && (
        <div key="log-list-message" className={styles['log__empty']}>
          {intl.formatMessage('No Logs')}
        </div>
      )}
    </div>
  )
}
